import { FormsEntities } from '@bimser/common';
import { extend, IDictionary, isNullOrUndefined } from '@bimser/core';
import { Map } from 'immutable';
import {
	IDataField,
	IListItem,
	IPropertyField,
	ISelectableListItem,
	MultiLanguageText,
	PrimitiveType
} from '../../../entities/common';
import { convertTypeToFieldType } from '../../../entities/common/IPropertyField';
import { IUserEvent } from '../../../entities/services';
import { geti18nValue } from '../../../helper';
import {
	IControlExecutedServerEvent,
	IPropertyChangedEventArgs,
	IPropertyChangingEventArgs
} from '../entities/EventArgs';
import { IComboBoxBaseProperties } from '../entities/IComboBoxBaseProperties';
import ListControl from './ListControl';
import { JSX } from 'react';
import _get from 'lodash/get';

export default class ComboBoxBase extends ListControl {
	allowClear: boolean;
	placeholder: MultiLanguageText;

	get selectedItem(): IListItem<MultiLanguageText> {
		return this.selectedItemValues?.length
			? this.__itemsCollection[this.selectedItemValues[0]]
			: null;
	}
	set selectedItem(item: IListItem<MultiLanguageText>) {
		if (this.items?.length && this.selectedItem !== item) {
			this.__proxyChanging.items = this.items.map(i => ({
				...i,
				selected: item.value === i.value
			}));
		}
	}

	get value(): any {
		const selectedItem = this.selectedItem;
		return selectedItem &&
			!isNullOrUndefined(selectedItem.value) &&
			selectedItem.value.toString() != ''
			? selectedItem.value
			: '';
	}
	set value(value: any) {
		if (this.items?.length && this.value !== value) {
			this.__proxyChanging.items = this.items.map(i => ({
				...i,
				selected: value == i.value
			}));
		}
	}

	get text(): string {
		const selectedItem = this.selectedItem;
		return selectedItem?.text?.getText ? selectedItem.text.getText() : '';
	}
	set text(text: string) {
		if (this.items?.length && this.text !== text) {
			this.__proxyChanging.items = this.items.map(i => ({
				...i,
				selected: text === i.text.getText()
			}));
		}
	}

	constructor() {
		super();
		this.allowClear = true;
		this.__excludeChangingProperties = ['selectedIndex', 'selectedItem', 'value', 'text'];

		this.subscribe(
			'system:PropertyChanging:items',
			(eventArgs: FormsEntities.IPropertyChangeEventArgs) =>
				new Promise<void>((resolve, reject) => {
					if (
						this.dataSourceType === 'dynamic' &&
						!this.__dataSourceLoadedAtLeastOnce &&
						!this.dataSource.runAtServer
					) {
						resolve();
					} else if (eventArgs.value) {
						const items: ISelectableListItem<IDictionary<string>>[] = eventArgs.value;
						const selectedItem = items.find(i => i.selected);

						const newText = selectedItem
							? geti18nValue(selectedItem.text, this.__form.currentLanguage)
							: undefined;

						const propertyChangeArgs = {
							name: this.name,
							newValue: eventArgs.value,
							oldValue: _get(this, eventArgs.dataField.join('.')),
							cancel: false
						};

						// TRIGGER PROPERTY CHANGING CLIENT EVENT
						this.emit<IPropertyChangingEventArgs<any>>(
							'event:OnPropertyChanging',
							propertyChangeArgs
						)
							.then(() => {
								if (!propertyChangeArgs.cancel) {
									const textArgument = {
										name: this.name,
										newValue: newText,
										oldValue: this.text,
										cancel: false
									};

									// TRIGGER TEXT CHANGING CLIENT EVENT
									this.emit<IPropertyChangingEventArgs<string>>(
										'event:OnTextChanging',
										textArgument
									)
										.then(() => {
											if (!textArgument.cancel) {
												// TRIGGER TEXT CHANGING SERVER EVENT
												this.emit<
													IUserEvent<IPropertyChangedEventArgs<string>>
												>('event:server:OnTextChanging', {
													argument: textArgument,
													controlId: this.name,
													name: 'TextChanging',
													sender: 'web'
												})
													.then(args => {
														const executedServerEvent: IControlExecutedServerEvent =
															args[0]?.executedServerEvent;
														if (
															!executedServerEvent ||
															!executedServerEvent?.parameters.cancel
														) {
															const valueArgument = {
																name: this.name,
																newValue: newText,
																oldValue: this.text,
																cancel: false
															};

															// TRIGGER VALUE CHANGING CLIENT EVENT
															this.emit<
																IPropertyChangingEventArgs<string>
															>(
																'event:OnValueChanging',
																valueArgument
															)
																.then(() => {
																	if (!valueArgument.cancel) {
																		// TRIGGER VALUE CHANGING SERVER EVENT
																		this.emit<
																			IUserEvent<
																				IPropertyChangedEventArgs<string>
																			>
																		>(
																			'event:server:OnValueChanging',
																			{
																				argument:
																					valueArgument,
																				controlId:
																					this.name,
																				name: 'ValueChanging',
																				sender: 'web'
																			}
																		)
																			.then(args => {
																				const executedServerEvent: IControlExecutedServerEvent =
																					args[0]
																						?.executedServerEvent;
																				if (
																					!executedServerEvent
																						?.parameters
																						?.cancel
																				) {
																					const mlNewValue =
																						new MultiLanguageText();
																					if (
																						selectedItem
																					) {
																						mlNewValue.setData(
																							selectedItem.text
																						);
																					}

																					const mlOldValue =
																						new MultiLanguageText();
																					if (
																						this
																							.selectedItem
																							?.text
																							?.getData
																					) {
																						mlOldValue.setData(
																							this.selectedItem.text.getData()
																						);
																					}

																					const selectedItemMLArgument =
																						{
																							name: this
																								.name,
																							newValue:
																								selectedItem
																									? {
																											...selectedItem,
																											text: mlNewValue
																									  }
																									: undefined,
																							oldValue:
																								this
																									.selectedItem
																									? {
																											...this
																												.selectedItem,
																											text: mlOldValue
																									  }
																									: undefined,
																							cancel: false
																						};

																					// TRIGGER SELECTED ITEM CHANGING CLIENT EVENT
																					this.emit<
																						IPropertyChangedEventArgs<
																							IListItem<MultiLanguageText>
																						>
																					>(
																						'event:OnSelectedItemChanging',
																						selectedItemMLArgument
																					)
																						.then(
																							() => {
																								if (
																									!selectedItemMLArgument.cancel
																								) {
																									const selectedItemArgument =
																										{
																											name: this
																												.name,
																											newValue:
																												selectedItem,
																											oldValue:
																												this
																													.selectedItem
																													? {
																															...this
																																.selectedItem,
																															text:
																																this
																																	.selectedItem
																																	.text
																																	?.getData &&
																																this.selectedItem.text?.getData()
																													  }
																													: undefined,
																											cancel: false
																										};

																									// TRIGGER SELECTED ITEM CHANGING SERVER EVENT
																									this.emit<
																										IUserEvent<
																											IPropertyChangedEventArgs<
																												IListItem<
																													IDictionary<string>
																												>
																											>
																										>
																									>(
																										'event:server:OnSelectedItemChanging',
																										{
																											argument:
																												selectedItemArgument,
																											controlId:
																												this
																													.name,
																											name: 'SelectedItemChanging',
																											sender: 'web'
																										}
																									)
																										.then(
																											args => {
																												const executedServerEvent: IControlExecutedServerEvent =
																													args[0]
																														?.executedServerEvent;
																												if (
																													!executedServerEvent ||
																													!executedServerEvent
																														?.parameters
																														.cancel
																												) {
																													resolve();
																												} else {
																													reject();
																												}
																											}
																										)
																										.catch(
																											() => {
																												reject();
																											}
																										);
																								} else {
																									reject();
																								}
																							}
																						)
																						.catch(
																							() => {
																								reject();
																							}
																						);
																				} else {
																					reject();
																				}
																			})
																			.catch(() => {
																				reject();
																			});
																	} else {
																		reject();
																	}
																})
																.catch(() => {
																	reject();
																});
														} else {
															reject();
														}
													})
													.catch(() => {
														reject();
													});
											} else {
												reject();
											}
										})
										.catch(() => {
											reject();
										});
								} else {
									reject();
								}
							})
							.catch(() => {
								reject();
							});
					} else {
						reject();
					}
				})
		);
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'placeholder', 'selectedItem.text'];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			allowClear: this.allowClear && !this.readOnly,
			placeholder: this.placeholder.getData()
		};
	}

	initialize() {
		super.initialize();
		this.placeholder = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'placeholder', culture, existCultures);
			}
		);
	}

	__loadProperties(data: IComboBoxBaseProperties) {
		const oldSelectedItem = this.selectedItem;
		const oldValue = this.value;
		const oldText = this.text;

		super.__loadProperties(data);

		if (data.placeholder) {
			this.placeholder.setData(data.placeholder);
		}

		this.__proxyChanged.allowClear = data.allowClear;

		const newSelectedItem = this.selectedItem;
		if (newSelectedItem?.value !== oldSelectedItem?.value && this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: newSelectedItem,
				oldValue: oldSelectedItem
			};
			this.emit<IPropertyChangedEventArgs<IListItem<MultiLanguageText>>>(
				'event:OnSelectedItemChanged',
				argument
			);

			const serverArgument = extend(true, {}, argument, {
				newValue: {
					...argument.newValue,
					text: argument.newValue?.text.getData()
				},
				oldValue: {
					...argument.oldValue,
					text: argument.oldValue?.text.getData()
				}
			});

			this.emit<IUserEvent<IPropertyChangedEventArgs<IListItem<IDictionary<string>>>>>(
				'event:server:OnSelectedItemChanged',
				{
					argument: serverArgument,
					controlId: this.name,
					name: 'SelectedItemChanged',
					sender: 'web'
				}
			);

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'selectedItem.value',
				newValue: this.value,
				oldValue,
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);

			if (this.data?.length) {
				const selectedField = this.data.find(
					i => i[this.dataSource.valueExpr] == this.value
				);
				const objectForKeys = this.data[0];

				Object.keys(objectForKeys).forEach(key => {
					const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
						name: 'selectedItem.field.' + key,
						newValue: selectedField ? selectedField[key] : undefined,
						oldValue,
						controlName: this.name
					};

					this.emit('event:OnPropertyChanged', propertyChangedArgs);
				});
			}
		}

		const newValue = this.value;
		if (oldValue != newValue && this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: newValue,
				oldValue: oldValue
			};

			this.emit<IPropertyChangedEventArgs<any>>('event:OnValueChanged', argument);
			this.emit<IUserEvent<IPropertyChangedEventArgs<any>>>('event:server:OnValueChanged', {
				argument,
				controlId: this.name,
				name: 'ValueChanged',
				sender: 'web'
			});

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'value',
				newValue: this.value,
				oldValue,
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);
		}

		const newText = this.text;
		if (oldText !== newText && this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: newText,
				oldValue: oldText
			};

			this.emit<IPropertyChangedEventArgs<string>>('event:OnTextChanged', argument);
			this.emit<IUserEvent<IPropertyChangedEventArgs<string>>>('event:server:OnTextChanged', {
				argument,
				controlId: this.name,
				name: 'TextChanged',
				sender: 'web'
			});

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'text',
				newValue: this.value,
				oldValue,
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);
		}
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields(properties);

		let valueType: PrimitiveType = this.valueType;
		if (properties) {
			valueType = properties.get('valueType');
		}

		let dataFields: IDataField[] = [];
		if (properties) {
			dataFields = properties.get('dataFields');
		}

		return [
			...fields,
			{
				name: 'allowClear',
				type: 'boolean'
			},
			{
				name: 'selectedItem',
				type: 'object',
				items: [
					{
						name: 'value',
						type: convertTypeToFieldType(valueType)
					},
					{
						name: 'text',
						type: 'string'
					},
					{
						name: 'field',
						type: 'object',
						items:
							dataFields?.map(i => ({
								name: i.name,
								type: i.type
							})) || [],
						readOnly: true
					}
				],
				readOnly: true
			}
		];
	}

	__render(children?: React.ReactNode): JSX.Element {
		return super.__render(children);
	}
}
